import { useEffect, useRef, useState } from 'react'
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js'
import { Phone, ShieldCheck, ShieldAlert, Loader2, Check } from 'lucide-react'
import { authApi } from '../../services/api'
import { useAuthStore } from '../../stores/authStore'

// OSPROD-179: the user's OWN mobile number with SMS verification, in
// Settings → Profile. Distinct from the read-only admin-assigned PSTN
// line (ProfilePhoneNumberField). Flow: enter number → server saves it
// (unverified) + sends an SMS code → enter code → Verified. Changing the
// number resets verification. The SMS dispatch is queue-based (notify),
// like email codes. All limits are server-config-driven.

// Region used only for formatting national input; the server re-parses
// and stores E.164. Configurable via Vite env so it tracks the backend's
// PHONE_DEFAULT_REGION.
const DEFAULT_REGION = (import.meta.env.VITE_PHONE_DEFAULT_REGION || 'US') as any

type Phase = 'view' | 'edit' | 'code'

export default function PhoneVerificationField() {
  const user = useAuthStore((s) => s.user)
  const fetchUser = useAuthStore((s) => s.fetchUser)

  const currentNumber = user?.phoneNumber || ''
  const verified = !!user?.phoneVerified

  const [phase, setPhase] = useState<Phase>('view')
  const [input, setInput] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [resendIn, setResendIn] = useState(0)
  const [pendingNumber, setPendingNumber] = useState('')

  // Resend cooldown ticker.
  useEffect(() => {
    if (resendIn <= 0) return
    const t = setInterval(() => setResendIn((n) => (n > 0 ? n - 1 : 0)), 1000)
    return () => clearInterval(t)
  }, [resendIn])

  const codeRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (phase === 'code') codeRef.current?.focus()
  }, [phase])

  const formatForDisplay = (e164: string) => {
    const parsed = parsePhoneNumberFromString(e164)
    return parsed ? parsed.formatInternational() : e164
  }

  const startEdit = () => {
    setError('')
    setInfo('')
    setInput(currentNumber ? formatForDisplay(currentNumber) : '')
    setPhase('edit')
  }

  const handleSendCode = async () => {
    setError('')
    setInfo('')
    const parsed = parsePhoneNumberFromString(input, DEFAULT_REGION)
    if (!parsed || !parsed.isValid()) {
      setError('Enter a valid phone number, including the country code.')
      return
    }
    setBusy(true)
    try {
      const res = await authApi.phone.start(parsed.number)
      setPendingNumber(res.data.phoneNumber)
      setResendIn(res.data.cooldownSec || 60)
      setCode('')
      setPhase('code')
      setInfo(`Code sent to ${formatForDisplay(res.data.phoneNumber)}.`)
      // Reflect the (now-unverified) pending number in the store.
      await fetchUser()
    } catch (err: any) {
      const code = err?.response?.data?.error
      if (code === 'invalid_phone_number') setError('That phone number is not valid.')
      else if (code === 'sms_send_failed') setError("Couldn't send the SMS right now. Try Resend in a moment.")
      else if (code === 'too_many_requests') setError('Too many code requests. Please try again later.')
      else if (err?.response?.status === 429) setError(`Please wait ${err?.response?.data?.retryAfterSec ?? 60}s before requesting another code.`)
      else setError('Failed to send the code. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const handleResend = async () => {
    if (resendIn > 0 || busy) return
    setError('')
    setInfo('')
    setBusy(true)
    try {
      const res = await authApi.phone.resend()
      setResendIn(res.data.cooldownSec || 60)
      setInfo('A new code is on its way.')
    } catch (err: any) {
      const code = err?.response?.data?.error
      if (code === 'max_resends') setError('Too many code requests. Change the number to start over.')
      else if (code === 'too_many_requests') setError('Too many code requests. Please try again later.')
      else if (err?.response?.status === 429) setError(`Please wait ${err?.response?.data?.retryAfterSec ?? 60}s.`)
      else if (code === 'no_pending_verification') { setError('Verification expired — re-enter your number.'); setPhase('edit') }
      else setError('Failed to resend. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const handleVerify = async () => {
    if (code.length < 4 || busy) return
    setError('')
    setInfo('')
    setBusy(true)
    try {
      await authApi.phone.verify(code)
      await fetchUser()
      setPhase('view')
      setInfo('Phone number verified.')
      setCode('')
    } catch (err: any) {
      const ecode = err?.response?.data?.error
      if (ecode === 'wrong_code') setError(`Incorrect code.${typeof err?.response?.data?.attemptsLeft === 'number' ? ` ${err.response.data.attemptsLeft} attempts left.` : ''}`)
      else if (ecode === 'code_expired') setError('That code expired. Tap Resend for a new one.')
      else if (ecode === 'too_many_attempts') setError('Too many attempts. Change the number to start over.')
      else if (ecode === 'phone_changed') { setError('The number changed — re-verify.'); setPhase('view') }
      else setError('Verification failed. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const handleRemove = async () => {
    setError('')
    setInfo('')
    setBusy(true)
    try {
      await authApi.phone.remove()
      await fetchUser()
      setPhase('view')
      setInfo('')
    } catch {
      setError('Failed to remove the number.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="md:col-span-2">
      <label className="block text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5">
        Phone number
      </label>

      {/* VIEW */}
      {phase === 'view' && (
        <div className="w-full bg-muted rounded px-3 py-2 border border-border min-h-[38px] flex items-center gap-3 flex-wrap">
          <Phone size={14} className="text-muted-foreground shrink-0" />
          {currentNumber ? (
            <>
              <span className="text-sm text-foreground">{formatForDisplay(currentNumber)}</span>
              {verified ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-500">
                  <ShieldCheck size={13} /> Verified
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-yellow-500">
                  <ShieldAlert size={13} /> Not verified
                </span>
              )}
              <div className="ml-auto flex items-center gap-3">
                {!verified && (
                  <button type="button" onClick={startEdit} className="text-xs text-primary hover:underline">Verify</button>
                )}
                <button type="button" onClick={startEdit} className="text-xs text-muted-foreground hover:text-foreground transition">Change</button>
                <button type="button" onClick={handleRemove} disabled={busy} className="text-xs text-destructive hover:underline disabled:opacity-50">Remove</button>
              </div>
            </>
          ) : (
            <>
              <span className="text-sm text-muted-foreground italic">No phone number added</span>
              <button type="button" onClick={startEdit} className="ml-auto text-xs text-primary hover:underline">Add phone</button>
            </>
          )}
        </div>
      )}

      {/* EDIT — enter / change the number */}
      {phase === 'edit' && (
        <div className="space-y-2">
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={input}
            onChange={(e) => setInput(new AsYouType(DEFAULT_REGION).input(e.target.value))}
            placeholder="+1 (555) 123-4567"
            autoFocus
            className="w-full bg-card text-foreground rounded px-3 py-2 text-sm border border-border focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSendCode}
              disabled={busy || !input.trim()}
              className="inline-flex items-center gap-2 rounded bg-primary text-primary-foreground px-3 py-1.5 text-sm font-medium hover:bg-primary/90 transition disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : null}
              Send code
            </button>
            <button type="button" onClick={() => { setPhase('view'); setError('') }} className="text-xs text-muted-foreground hover:text-foreground transition">Cancel</button>
          </div>
        </div>
      )}

      {/* CODE — enter the SMS code */}
      {phase === 'code' && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Enter the code sent to <span className="text-foreground font-medium">{formatForDisplay(pendingNumber)}</span>.
          </p>
          <input
            ref={codeRef}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder="123456"
            className="w-full bg-card text-foreground rounded px-3 py-2.5 text-center text-lg tracking-[0.5em] border border-border focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleVerify}
              disabled={busy || code.length < 4}
              className="inline-flex items-center gap-2 rounded bg-primary text-primary-foreground px-3 py-1.5 text-sm font-medium hover:bg-primary/90 transition disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Confirm
            </button>
            <button
              type="button"
              onClick={handleResend}
              disabled={resendIn > 0 || busy}
              className="text-xs text-muted-foreground hover:text-foreground transition disabled:opacity-50"
            >
              {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
            </button>
            <button type="button" onClick={() => { setPhase('view'); setError('') }} className="ml-auto text-xs text-muted-foreground hover:text-foreground transition">Cancel</button>
          </div>
        </div>
      )}

      {error && <p className="text-destructive text-xs mt-1.5">{error}</p>}
      {!error && info && <p className="text-green-500 text-xs mt-1.5">{info}</p>}
    </div>
  )
}
